import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Building2, User, Phone, Mail, MapPin, Wallet, Loader2, CheckCircle2, ArrowRight, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartnerSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState(searchParams.get('customer_id') || '');
  const [minAmount, setMinAmount] = useState(1000);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    // Fetch signup config
    fetch(`${API}/api/portal/auth/signup-config`)
      .then(r => r.json())
      .then(data => {
        setMinAmount(data.signup_min_amount || 1000);
        setSignupEnabled(data.signup_enabled !== false);
      })
      .catch(() => {});

    if (searchParams.get('payment') === 'failed' && searchParams.get('customer_id')) {
      setCustomerId(searchParams.get('customer_id'));
      setStep(2);
      toast.error('Payment failed. Please try again.');
    }
  }, [searchParams]);

  // Extract proper error message
  const getError = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || d.message).filter(Boolean).join(', ') || fallback;
    if (data.message) return data.message;
    return fallback;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.phone || !form.email || !form.address) {
      toast.error('Please fill all required fields');
      return;
    }
    if (form.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    if (!form.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCustomerId(data.customer_id);
        setStep(2);
        toast.success('Registration successful! Complete payment to activate.');
      } else {
        toast.error(getError(data, 'Registration failed. Please check your details.'));
      }
    } catch {
      toast.error('Unable to connect to server. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!customerId) {
      toast.error('Registration not found. Please register again.');
      setStep(1);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/auth/register/initiate-payment?customer_id=${customerId}&amount=${minAmount}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success && data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        toast.error(getError(data, 'Payment initiation failed. Please try again.'));
      }
    } catch {
      toast.error('Unable to connect to server. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    if (field === 'phone') value = value.replace(/\D/g, '').slice(0, 10);
    if (field === 'pincode') value = value.replace(/\D/g, '').slice(0, 6);
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logo.png" alt="ANT Broadband" className="h-9 w-auto" />
          <div>
            <h1 className="font-bold text-lg text-slate-900 tracking-tight">Partner Onboarding</h1>
            <p className="text-xs text-slate-500">MSO / Partner Registration</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {!signupEnabled ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="mx-auto h-12 w-12 text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">Registration is currently closed</p>
              <p className="text-sm text-slate-400 mt-1">Please contact support to create your account.</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/partner')}>Go to Login</Button>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${step >= 1 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
            <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</span>
            Details
          </div>
          <div className="w-8 h-px bg-slate-200" />
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${step >= 2 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-300 text-white'}`}>2</span>
            Payment
          </div>
        </div>

        {/* Step 1: Registration Form */}
        {step === 1 && (
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Register Your Business</CardTitle>
              <CardDescription>Fill in your details to create your partner account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <Building2 className="h-3.5 w-3.5 text-slate-400" /> Company Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="Your company / business name"
                    value={form.company_name}
                    onChange={e => updateField('company_name', e.target.value)}
                    data-testid="signup-company-name"
                    required
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <User className="h-3.5 w-3.5 text-slate-400" /> Contact Person <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="Full name"
                    value={form.contact_name}
                    onChange={e => updateField('contact_name', e.target.value)}
                    data-testid="signup-contact-name"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1.5 mb-1.5">
                      <Phone className="h-3.5 w-3.5 text-slate-400" /> Phone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="10-digit mobile"
                      value={form.phone}
                      onChange={e => updateField('phone', e.target.value)}
                      maxLength={10}
                      data-testid="signup-phone"
                      required
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5 mb-1.5">
                      <Mail className="h-3.5 w-3.5 text-slate-400" /> Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="email"
                      placeholder="email@company.com"
                      value={form.email}
                      onChange={e => updateField('email', e.target.value)}
                      data-testid="signup-email"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" /> Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="Full address"
                    value={form.address}
                    onChange={e => updateField('address', e.target.value)}
                    data-testid="signup-address"
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">City</Label>
                    <Input placeholder="City" value={form.city} onChange={e => updateField('city', e.target.value)} data-testid="signup-city" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">State</Label>
                    <Input placeholder="State" value={form.state} onChange={e => updateField('state', e.target.value)} data-testid="signup-state" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Pincode</Label>
                    <Input placeholder="6-digit" value={form.pincode} onChange={e => updateField('pincode', e.target.value)} maxLength={6} data-testid="signup-pincode" />
                  </div>
                </div>

                {/* Info card */}
                <div className="bg-blue-50 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-2">
                    <Wallet className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium">Mandatory Wallet Top-up: Rs.{minAmount.toLocaleString('en-IN')}</p>
                      <p className="mt-0.5 text-blue-600">After registration, you'll be redirected to make a Rs.{minAmount.toLocaleString('en-IN')} wallet payment via Easebuzz. This will be your wallet balance for OTT pack activations.</p>
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Continue to Payment
                </Button>

                <p className="text-center text-xs text-slate-500 mt-3">
                  Already have an account?{' '}
                  <button type="button" className="text-blue-600 hover:underline font-medium" onClick={() => navigate('/partner')}>
                    Login here
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
          <Card>
            <CardHeader className="pb-4 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <CardTitle className="text-xl">Registration Complete!</CardTitle>
              <CardDescription>Complete the mandatory wallet top-up to activate your account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-500">Wallet Top-up Amount</span>
                  <span className="text-2xl font-bold text-slate-900">Rs.{minAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="space-y-1 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    Instant account activation
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    Access to all OTT packs
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    Wallet balance for subscriptions
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Shield className="h-3.5 w-3.5" />
                Secure payment via Easebuzz
              </div>

              <Button onClick={handlePayment} className="w-full" size="lg" disabled={loading} data-testid="signup-pay-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wallet className="h-4 w-4 mr-2" />}
                Pay Rs.{minAmount.toLocaleString('en-IN')} & Activate Account
              </Button>

              <p className="text-center text-xs text-slate-400">
                {searchParams.get('payment') === 'failed' && (
                  <Badge variant="outline" className="text-red-600 border-red-200 mb-2">Previous payment failed. Please try again.</Badge>
                )}
              </p>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </main>
    </div>
  );
}
